import { PageTitle, useIntl } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { FC, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getDispatched } from "../../stock-managment/stock-management-helper"
import customerStore from "features/organizations/customers/store/customer-store"
import { convertDateToDDMMMYYYY } from "components/utils/date-formatter-utils"
import sensorBatchStore from "features/iot/sensor-batch/stores/sensor-batch-store"
import ListView, { TemplateItem } from "components/utils/list-view/list-view"
import LowSpinner from "components/common/low-spinner"
import { NotFound } from "components/utils/not-found"
import { itemViewListContainerStyles } from "components/common-style/common-style"

interface DispatchedHistoryRow {
    id: string
    serialNumber: string
    macAddress: string
    customerName: string | undefined
    siteName: string | undefined
    dispatchDate: string | undefined
}

const DispatchedSensorsListView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { partNumber, customerName, siteName } = useParams()
    const { sensorBatches } = sensorBatchStore

    const title = `Dispatched Sensors For PartNumber: ${partNumber}`

    const listOfSensors = useMemo(() => {
        if (!partNumber || sensorBatches.length === 0) return []

        const dispatchedSensors = getDispatched(partNumber)
        if (!dispatchedSensors) return []

        const filteredData = dispatchedSensors.filter(({ customerId, siteId }) => {
            const sdCustomerName = customerStore.getCustomerNameById(customerId) || "unknown"
            const sdSiteName = customerStore.getSiteNameById(siteId) || "unknown"

            return sdCustomerName === customerName?.toLowerCase() && sdSiteName === siteName?.toLowerCase()
        })

        return filteredData.map(({ macAddress, serialNumber, customerId, siteId, dispatchDate }) => ({
            id: macAddress,
            serialNumber,
            macAddress,
            customerName: customerStore.getCustomerNameById(customerId) || "unknown",
            siteName: customerStore.getSiteNameById(siteId) || "unknown",
            dispatchDate: dispatchDate ? convertDateToDDMMMYYYY(dispatchDate) : undefined,
        }))
    }, [partNumber, sensorBatches, customerName, siteName])

    const breadcrumbsPath = [
        {
            text: formatMessage("dispatchHistory.title"),
            to: `/stock-management/dispatch-history/${partNumber}`,
        },
        {
            text: partNumber as string,
            to: `/stock-management/dispatched-sensors/${partNumber}`,
        },
    ]

    const template: TemplateItem<DispatchedHistoryRow>[] = [
        {
            headingKey: "headings.serialNumber",
            cellFunc: (a: DispatchedHistoryRow) => a.serialNumber,
            isSearchable: true,
        },
        {
            headingKey: "headings.macAddress",
            cellFunc: (a: DispatchedHistoryRow) => a.macAddress,
            isSearchable: true,
        },
        {
            headingKey: "headings.customer",
            cellFunc: (a: DispatchedHistoryRow) => a.customerName,
        },
        {
            headingKey: "headings.site",
            cellFunc: (a: DispatchedHistoryRow) => a.siteName,
        },
        {
            headingKey: "headings.DispatchDate",
            cellFunc: (a: DispatchedHistoryRow) => a.dispatchDate,
            isSearchable: true,
        },
    ]

    return sensorBatches.length < 1 ? (
        <LowSpinner id={"sensorBatch.loading"} />
    ) : !listOfSensors.length ? (
        <NotFound id={title} />
    ) : (
        <>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={title} />
            <div style={itemViewListContainerStyles}>
                <ListView data={listOfSensors || []} noneYetKey='sensor.notFound' template={template} />
            </div>
        </>
    )
})

export default DispatchedSensorsListView
